import FetchService from "../FetchService/CustomService";
import { getErrorMessage, detectMobile } from "../utils";
import usePostJobSuccessStore from "../../zustandStores/usePostJobSuccessStore";
import { useJobPostingStepperStore } from "../../zustandStores/jobPostingStepperStore";
import { useJobPostingStore } from "../../zustandStores/jobPostingStore";
import { useTemporaryJobPostingStore } from "../../zustandStores/temporaryJobPostingStore";
import useModalStore from "../../zustandStores/useModalStore";
import LogService from "../LogService";
import {
  JOB_POST_SUCCESS,
  AUTO_SAVE_JOB_POSTED,
  JOB_POST_EDIT
} from "../webengage/webEngageConstants";
import { isJobEditFromUrl } from "components/JobPostingv2/utils";
import { getCookie } from "services/cookie";

const BaseUrl = process.env.BASE_URL;
const JobPostingService = {
  async getLatestJobTitle() {
    const url = BaseUrl + "/recruiter/latest_job_title";
    const loaderConfig = { hasLoader: false };
    try {
      const res = await FetchService.getCall({
        url,
        loaderConfig
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },
  async getLatestJobTitleV2() {
    const url = BaseUrl + "/recruiter/v2/latest_job_title";
    const loaderConfig = { hasLoader: false };
    try {
      const res = await FetchService.getCall({
        url,
        loaderConfig
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },
  async getAverageSalary(params = {}) {
    const url = BaseUrl + "/recruiter/job/city_wise_avg_salary";
    try {
      const res = await FetchService.getCall({
        url,
        query: { city_id: params.cityId, category_id: params.categoryId }
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },
  async getScreeningQuestions({ jobId }) {
    const url = BaseUrl + "/recruiter/job/get-screening-questions";
    try {
      const res = await FetchService.postCall({
        url,
        body: { jobId: Number(jobId) }
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },
  async postScreeningQuestions({ jobId, questionIds }) {
    const url = BaseUrl + "/recruiter/job/post-screening-questions";
    try {
      const res = await FetchService.postCall({
        url,
        body: { jobId: Number(jobId), questionIds: questionIds }
      });

      return res;
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },
  async getScreeningAnswers(payload = {}) {
    const url = BaseUrl + "/recruiter/job/candidate-screening-questions";
    const loaderConfig = { hasLoader: true };
    try {
      const res = await FetchService.postCall({
        url,
        body: payload,
        loaderConfig
      });
      return res?.data?.data || [];
    } catch (error) {
      return Promise.reject(getErrorMessage(error));
    }
  },
  async boostJob({ jobId }) {
    const url = BaseUrl + "/recruiter/job/v1/plan";
    const loaderConfig = { hasLoader: true };
    try {
      const res = await FetchService.postCall({
        url,
        body: {
          jobId: Number(jobId),
          planType: "miniBoost",
          source: "RECRUITER"
        },
        loaderConfig
      });
      return res?.data?.data || [];
    } catch (error) {
      return Promise.reject(error);
    }
  },
  async getEnrichedJobTemplate({ categoryId, variationId }) {
    const url =
      BaseUrl +
      `/v2/metadata/enhanced-static-jd-templates?category_id=${categoryId}&variation=${variationId}`;

    const loaderConfig = { hasLoader: true };
    try {
      const res = await FetchService.getCall({
        url,
        loaderConfig
      });
      return res?.data?.data || {};
    } catch (error) {
      return Promise.reject(getErrorMessage(error));
    }
  },
  async getFamousLocationData({ categoryId, cityId, localityId }) {
    const url = BaseUrl + "/v2/metadata/famous-location";
    const loaderConfig = { hasLoader: false };
    try {
      const res = await FetchService.getCall({
        url,
        query: {
          category_id: categoryId,
          city_id: cityId,
          locality_id: localityId
        },
        loaderConfig
      });
      return res?.data?.data || [];
    } catch (error) {
      console.log("service error", error);
      return Promise.reject(getErrorMessage(error));
    }
  },

  // Silent job posting - only API call, no side effects
  async postJobSilent(jobData, options = {}) {
    const url = BaseUrl + "/recruiter/job/job-post";

    try {
      const headers = {
        "device-source": detectMobile() ? "M-site" : "Desktop",
        "device-id":getCookie("recDeviceId")
      };

      const payload = jobData;
      payload.job_id = undefined;

      const response = await FetchService.postCall({
        url,
        body: payload,
        headers,
        loaderConfig: { hasLoader: false } // No loader for silent calls
      });

      return response.data;
    } catch (error) {
      console.error("Silent job posting error:", error);
      return Promise.reject(getErrorMessage(error));
    }
  },

  isJobEditAndDraftFromUrl() {
    if (typeof window === "undefined") return false;

    const urlParams = new URLSearchParams(window.location.search);
    const jobId = urlParams.get("job_id");
    const rePost = urlParams.get("re_post") === "true";
    const isDraft = urlParams.get("is_draft") === "true";

    return (!!jobId || isDraft) && !rePost;
  },

  // V2 Job Posting Method - No Redux, uses Zustand stores directly
  async postJobV2(jobData, options = {}) {
    const {
      isAutoJobPosted = false,
      old_job_id = null,
      allowDuplicate = false,
      repost_campaign,
      successCallback,
      errorCallback,
      postJobNow = false
    } = options;

    const { category_id = "" } = jobData;
    const isEdit = this.isJobEditAndDraftFromUrl();
    const url = isEdit
      ? BaseUrl + "/recruiter/job/job-edit"
      : BaseUrl + "/recruiter/job/job-post";
    if (!isEdit) {
      jobData.job_id = undefined;
    }
    try {
      // Clear previous job post data
      usePostJobSuccessStore.getState().clearResponseData();

      // Check if current step is confirmation to auto-add post_job_now
      const { activeTabId } = useJobPostingStepperStore.getState();
      const shouldPostJobNow = activeTabId === "confirmation";

      const headers = {
        "device-source": detectMobile() ? "M-site" : "Desktop",
        "device-id": getCookie("recDeviceId")
      };

      // if (allowDuplicate) {
      //   headers["allow-duplicate"] = "true";
      // }

      const payload =
        shouldPostJobNow || postJobNow
          ? { ...jobData, post_job_now: true }
          : jobData;

      const response = await FetchService.postCall({
        url,
        body: payload,
        headers
      });

      const {
        tab = "live",
        job_id = "",
        is_draft = false,
        draft_id = "",
        is_screening_applicable = false,
        communication = ""
      } = response.data.data;

      // Update Zustand store with response data
      usePostJobSuccessStore.getState().setResponseData({
        job_id,
        is_draft,
        isScreeningApplicable: is_screening_applicable,
        category_id,
        tab,
        isEdit: isJobEditFromUrl(),
        communication
      });

      // Event tracking for V2 system
      let eventInfo = {};
      let jobType = "free";

      // Determine job type based on jobPlan
      if (jobData.jobPlan === "PREMIUM") {
        jobType = "paid";
      } else if (jobData.is_repost) {
        jobType = "repost";
      }

      if (jobData.is_repost) {
        eventInfo = {
          ...jobData,
          new_job: job_id,
          old_job: old_job_id,
          posted_from: "V2_SYSTEM"
        };
      } else {
        eventInfo = {
          ...jobData,
          posted_from: "V2_SYSTEM"
        };
      }

      const eventName = jobData.job_id ? JOB_POST_EDIT : JOB_POST_SUCCESS;

      if (!jobData.job_id) {
        eventInfo = {
          ...eventInfo,
          job_post_type: jobType
        };
      }

      if (repost_campaign) {
        eventInfo = {
          ...eventInfo,
          from_repost_campaigin: !!repost_campaign,
        };
      }

      // Log events using LogService
      LogService.log(eventName, eventInfo);

      if (isAutoJobPosted) {
        LogService.log(AUTO_SAVE_JOB_POSTED, {
          ...jobData,
          posted_from: "V2_SYSTEM"
        });
      }

      // Execute success callback if provided
      if (successCallback) {
        successCallback({
          job_id,
          is_draft,
          isScreeningApplicable: is_screening_applicable,
          tab,
          draft_id
        });
      }

      // Clear job posting stores and stepper store after successful job posting
      setTimeout(() => {
        useJobPostingStore.getState().clearStore();
        useTemporaryJobPostingStore.getState().clearStore();
        useJobPostingStepperStore.getState().resetStore();
      }, 500);

      return {
        job_id,
        isDraft: is_draft,
        isScreeningApplicable: is_screening_applicable,
        tab,
        draft_id,
        response: response.data
      };
    } catch (error) {
      console.error("V2 Job posting error:", error);

      let errorMessage = getErrorMessage(error);
      const errorResponse = error && error.response;
      const { details = {}, status = "" } =
        errorResponse && errorResponse.data ? errorResponse.data : {};

      // Execute error callback if provided
      if (errorCallback) {
        errorCallback({ error, details, errorMessage });
      }

      return Promise.reject({ error, details, errorMessage });
    } finally {
      // Always reset modal store regardless of success or failure
      useModalStore.getState().resetStore();
    }
  }
};

export default JobPostingService;
